import { useEffect, useMemo, useRef, useState } from "react";
import PiChevronUpStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronUpStroke";
import PiChevronDownStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronDownStroke";
import PiTagStroke from "@markeroo/icons/src/stroke/general/PiTagStroke";
import { useHighlightNavigation } from "../contexts/HighlightNavigationContext";
import { HighlightList } from "./HighlightList";
import { HighlightProgressBar } from "./HighlightProgressBar";
import type { HighlightWithMetadata } from "../types/highlightNavigation";
import { ui } from "@/strings";
import { getScrollBehavior } from "@/utils/platform";
import type { CriteriaCategory } from "@markeroo/theme";

interface TagPaletteEntry {
  key: string;
  label?: string | null;
  color: string;
  humanDescription?: string | null;
  category?: string | null;
}

interface HighlightNavigationSidebarProps {
  isTeacher: boolean;
  onEditHighlight?: (highlight: HighlightWithMetadata) => void;
  onDeleteHighlight?: (highlight: HighlightWithMetadata) => void;
  tagPalette?: TagPaletteEntry[];
}

interface LegendPopoverProps {
  tagPalette: TagPaletteEntry[];
  orderedHighlights: HighlightWithMetadata[];
  hoveredLegendKey: string | null;
  setHoveredLegendKey: (key: string | null) => void;
}

// Ordered list of categories for consistent display
const CATEGORY_ORDER: CriteriaCategory[] = [
  "claims",
  "support",
  "reasoning",
  "flow",
  "style",
  "mechanics",
  "other",
];

function LegendPopover({
  tagPalette,
  orderedHighlights,
  hoveredLegendKey,
  setHoveredLegendKey,
}: LegendPopoverProps) {
  // Calculate which legend keys have highlights in the paper
  const keysWithHighlights = useMemo(() => {
    const keys = new Set<string>();
    for (const highlight of orderedHighlights) {
      if (highlight.type === "ai" && highlight.tagId) {
        keys.add(highlight.tagId);
      }
    }
    return keys;
  }, [orderedHighlights]);

  // Sort palette by category order, then by whether they have highlights
  const sortedPalette = useMemo(() => {
    return [...tagPalette].sort((a, b) => {
      // First sort by category order
      const aCat = (a.category as CriteriaCategory) || "other";
      const bCat = (b.category as CriteriaCategory) || "other";
      const aIndex = CATEGORY_ORDER.indexOf(aCat);
      const bIndex = CATEGORY_ORDER.indexOf(bCat);
      if (aIndex !== bIndex) return aIndex - bIndex;

      // Within same category, sort by whether they have highlights
      const aHas = keysWithHighlights.has(a.key);
      const bHas = keysWithHighlights.has(b.key);
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return 0;
    });
  }, [tagPalette, keysWithHighlights]);

  return (
    <div className="absolute right-0 top-full z-50 pt-2 w-[95vw] sm:w-[90vw] max-w-4xl">
      <div className="rounded-lg border border-theme-primary bg-white p-3 sm:p-4 shadow-lg max-h-[70vh] overflow-y-auto">
        <h3 className="mb-2 sm:mb-3 text-sm sm:text-base font-semibold text-theme-primary">{ui.labels.legend}</h3>
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          onMouseLeave={() => setHoveredLegendKey(null)}
        >
          {sortedPalette.map((def) => {
            const hasHighlights = keysWithHighlights.has(def.key);
            const isHovered = hoveredLegendKey === def.key;
            return (
              <li
                key={def.key}
                className={`flex flex-col gap-1 rounded-lg border m-1.5 p-3 text-sm shadow-sm transition-all duration-150 ${
                  hasHighlights
                    ? isHovered
                      ? "cursor-pointer border-hover bg-white ring-2 ring-border-hover"
                      : "cursor-pointer border bg-surface-secondary hover:border-border-hover hover:bg-white"
                    : "cursor-default border-border bg-surface-secondary/50 opacity-50"
                }`}
                onMouseEnter={() => hasHighlights && setHoveredLegendKey(def.key)}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-3 w-3 flex-shrink-0 rounded-full ${!hasHighlights ? "opacity-50" : ""}`}
                    style={{ backgroundColor: def.color }}
                  />
                  <span className={`font-semibold ${hasHighlights ? "text-theme-secondary" : "text-theme-muted"}`}>
                    {def.label ?? def.key}
                  </span>
                </div>
                {def.humanDescription && (
                  <span className={`text-xs ${hasHighlights ? "text-theme-muted" : "text-theme-muted"}`}>
                    {def.humanDescription}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function HighlightNavigationSidebar({
  isTeacher,
  onEditHighlight,
  onDeleteHighlight,
  tagPalette,
}: HighlightNavigationSidebarProps) {
  const [showLegend, setShowLegend] = useState(false);
  const {
    selectedHighlightId,
    orderedHighlights,
    currentIndex,
    selectHighlight,
    deselectHighlight,
    toggleComplete,
    navigateNext,
    navigatePrevious,
    showCompleted,
    setShowCompleted,
    hoveredLegendKey,
    setHoveredLegendKey,
    canUnresolve,
  } = useHighlightNavigation();

  if (orderedHighlights.length === 0) {
    return null;
  }

  const completedCount = orderedHighlights.filter((h) => h.isCompleted).length;
  const unresolvedCount = orderedHighlights.length - completedCount;
  const showAllResolvedMessage = !isTeacher && orderedHighlights.length > 0 && unresolvedCount === 0;
  const listContainerRef = useRef<HTMLDivElement | null>(null);

  // Get the currently selected highlight for screen reader announcements
  const selectedHighlight = orderedHighlights.find((h) => h.id === selectedHighlightId);

  // Keep the selected sidebar item comfortably in view (around 40% down)
  useEffect(() => {
    if (!selectedHighlightId || !listContainerRef.current) {
      return;
    }

    const container = listContainerRef.current;
    const item = container.querySelector(
      `[data-highlight-list-item-id="${selectedHighlightId}"]`,
    );

    if (!item) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const relativeTop = itemRect.top - containerRect.top;
    const targetOffset = container.clientHeight * 0.4;
    const delta = relativeTop - targetOffset;
    const nextScrollTop = container.scrollTop + delta;

    container.scrollTo({
      top: nextScrollTop,
      behavior: getScrollBehavior(),
    });
  }, [selectedHighlightId]);

  return (
    <div
      className="flex flex-col h-full w-full lg:w-80 lg:border-l border-theme-primary bg-surface-secondary"
      onKeyDown={(e) => {
        if (e.key === "Escape" && selectedHighlightId) {
          e.preventDefault();
          e.stopPropagation();
          deselectHighlight();
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-theme-primary bg-white">
        <div className="flex flex-col min-w-0">
          <h2 className="text-xs sm:text-sm font-semibold text-theme-primary truncate">
            {ui.highlightNavigation.title}
          </h2>
          <p className="text-xs text-theme-muted truncate">
            {ui.highlightNavigation.highlightCount(orderedHighlights.length)}
            {!isTeacher && completedCount > 0 && (
              <>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <span> • </span>
                {ui.highlightNavigation.highlightCount(completedCount)}{" "}
                {ui.highlightNavigation.completedLabel}
              </>
            )}
          </p>
          {/* Show resolved toggle (students only, below count) */}
          {!isTeacher && (
            <div className="mt-1">
              <label className="flex items-center gap-1 text-xs text-theme-secondary">
                <input
                  type="checkbox"
                  className="h-3 w-3 rounded border-border text-primary focus:ring-primary"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  aria-label={ui.highlightNavigation.showResolved}
                />
                <span>{ui.highlightNavigation.showResolved}</span>
              </label>
            </div>
          )}
        </div>

        {/* Top right controls */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Legend button */}
          {tagPalette && tagPalette.length > 0 && (
            <div
              className="relative"
              onMouseEnter={() => setShowLegend(true)}
              onMouseLeave={() => {
                setShowLegend(false);
                setHoveredLegendKey(null);
              }}
            >
              <button
                type="button"
                className="flex items-center gap-1 sm:gap-2 rounded-lg border border-theme-primary bg-white px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-theme-secondary shadow-sm transition hover:border-border-hover hover:bg-surface-secondary"
                aria-label={ui.labels.legend}
              >
                <PiTagStroke className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{ui.labels.legend}</span>
              </button>
              {showLegend && (
                <LegendPopover
                  tagPalette={tagPalette}
                  orderedHighlights={orderedHighlights}
                  hoveredLegendKey={hoveredLegendKey}
                  setHoveredLegendKey={setHoveredLegendKey}
                />
              )}
            </div>
          )}

          {/* Navigation controls */}
          {selectedHighlightId && currentIndex !== null && (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                onClick={navigatePrevious}
                className="p-1 text-theme-secondary hover:text-theme-primary hover:bg-surface-secondary rounded transition-colors"
                aria-label={ui.highlightNavigation.previousHighlight}
                title={ui.highlightNavigation.previousHighlight}
              >
                <PiChevronUpStroke size={20} />
              </button>
              <button
                onClick={navigateNext}
                className="p-1 text-theme-secondary hover:text-theme-primary hover:bg-surface-secondary rounded transition-colors"
                aria-label={ui.highlightNavigation.nextHighlight}
                title={ui.highlightNavigation.nextHighlight}
              >
                <PiChevronDownStroke size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation hint */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b border-theme-primary bg-white">
        <p className="text-xs text-theme-muted text-center leading-tight">
          {selectedHighlightId && currentIndex !== null ? (
            <>
              {ui.highlightNavigation.highlightPosition(
                currentIndex + 1,
                orderedHighlights.length,
              )}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <> • Press <kbd className="px-1 py-0.5 bg-surface-secondary rounded text-theme-secondary">Tab</kbd> to navigate</>
            </>
          ) : (
            /* eslint-disable-next-line no-restricted-syntax */
            <>Press <kbd className="px-1 py-0.5 bg-surface-secondary rounded text-theme-secondary">Tab</kbd> to navigate</>
          )}
        </p>
        {/* Screen reader announcement for highlight navigation */}
        {selectedHighlightId && currentIndex !== null && (
          <div role="status" aria-live="polite" className="sr-only">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <>Highlight {currentIndex + 1} of {orderedHighlights.length}
            {selectedHighlight && `: ${selectedHighlight.phrase.substring(0, 50)}`}</>
          </div>
        )}
      </div>

      {/* Progress bar (students only) */}
      {!isTeacher && (
        <HighlightProgressBar
          resolvedCount={completedCount}
          totalCount={orderedHighlights.length}
        />
      )}

      {/* Highlight list */}
      <div ref={listContainerRef} className="flex-1 overflow-y-auto p-2 sm:p-3">
        {showAllResolvedMessage && (
          <div className="mb-3 rounded-md border border-semantic-success-border bg-semantic-success-bg px-3 py-2">
            <p className="text-xs font-semibold text-semantic-success-text">
              {ui.highlightNavigation.allResolvedTitle}
            </p>
            <p className="mt-0.5 text-xs text-semantic-success-text">
              {ui.highlightNavigation.allResolvedMessage}
            </p>
          </div>
        )}
        <HighlightList
          highlights={orderedHighlights}
          selectedHighlightId={selectedHighlightId}
          isTeacher={isTeacher}
          showCompleted={isTeacher ? true : showCompleted}
          onSelectHighlight={selectHighlight}
          onToggleComplete={!isTeacher ? toggleComplete : undefined}
          onEditHighlight={isTeacher ? onEditHighlight : undefined}
          onDeleteHighlight={isTeacher ? onDeleteHighlight : undefined}
          canUnresolve={!isTeacher ? canUnresolve : undefined}
        />
      </div>

    </div>
  );
}
